import React from 'react';
import { ShoppingBag, Shield, Bus, Phone, Stamp, AlertCircle } from 'lucide-react';

const AdditionalOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  // Описания уровней шоппинга
  const shoppingOptions = {
    'minimal': {
      name: 'Минимальный',
      description: 'Только необходимые покупки и несколько сувениров. Около 1000 ₽ на человека.',
      icon: <ShoppingBag className="w-4 h-4 text-green-500" />
    },
    'moderate': {
      name: 'Средний',
      description: 'Сувениры и небольшой шоппинг. Около 3000 ₽ на человека.',
      icon: <ShoppingBag className="w-4 h-4 text-yellow-500" />
    },
    'extensive': {
      name: 'Максимальный',
      description: 'Активный шоппинг, включая одежду, сувениры и местные товары. От 8000 ₽ на человека.',
      icon: <ShoppingBag className="w-4 h-4 text-red-500" />
    }
  };

  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ShoppingBag className="w-5 h-5 text-green-500 mr-2" />
        Дополнительные расходы
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Шоппинг */}
        <div className="col-span-full flex items-start mb-2">
          <input
            type="checkbox"
            id="includeShoppingBudget"
            name="includeShoppingBudget"
            checked={formData.includeShoppingBudget}
            onChange={handleInputChange}
            className="form-checkbox mt-1"
          />
          <div className="ml-2">
            <label htmlFor="includeShoppingBudget" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Бюджет на шоппинг и сувениры
            </label>
            {formData.includeShoppingBudget && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {Object.entries(shoppingOptions).map(([key, option]) => (
                  <label 
                    key={key}
                    className={`
                      flex items-center p-2 rounded-lg border
                      ${formData.shoppingBudget === key 
                        ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/40 dark:border-green-700 dark:text-green-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-650'}
                      transition-colors cursor-pointer
                    `}
                  >
                    <input
                      type="radio"
                      name="shoppingBudget"
                      value={key}
                      checked={formData.shoppingBudget === key}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="mr-2">
                      {option.icon}
                    </div>
                    <span className="text-xs font-medium">{option.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Страховка */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeTravelInsurance"
              checked={formData.includeTravelInsurance}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <div className="ml-2">
              <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <Shield className="w-4 h-4 text-blue-500 mr-1" />
                Страховка
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                ~500 ₽ на человека в день
              </p>
            </div>
          </label>
        </div>

        {/* Местный транспорт */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeLocalTransport"
              checked={formData.includeLocalTransport}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <div className="ml-2">
              <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <Bus className="w-4 h-4 text-purple-500 mr-1" />
                Местный транспорт
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                ~500 ₽ на человека в день
              </p>
            </div>
          </label>
        </div>

        {/* Международный роуминг */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeInternationalRoaming"
              checked={formData.includeInternationalRoaming}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <div className="ml-2">
              <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <Phone className="w-4 h-4 text-blue-500 mr-1" />
                Роуминг/Интернет
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                ~400 ₽ на человека в день
              </p>
            </div>
          </label>
        </div>

        {/* Виза */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeVisaCost"
              checked={formData.includeVisaCost}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <div className="ml-2">
              <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <Stamp className="w-4 h-4 text-red-500 mr-1" />
                Стоимость визы
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                ~5000 ₽ на человека
              </p>
            </div>
          </label>
        </div>

        {/* Резервный фонд */}
        <div className="col-span-full flex items-start mt-2">
          <input
            type="checkbox"
            id="includeEmergencyFund"
            name="includeEmergencyFund"
            checked={formData.includeEmergencyFund}
            onChange={handleInputChange}
            className="form-checkbox mt-1"
          />
          <div className="ml-2 flex-grow">
            <label htmlFor="includeEmergencyFund" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <AlertCircle className="w-4 h-4 text-orange-500 mr-1" />
              Резервный фонд на непредвиденные расходы
            </label>
            {formData.includeEmergencyFund && (
              <div className="mt-2 flex items-center">
                <input
                  type="range"
                  id="emergencyFundPercent"
                  name="emergencyFundPercent"
                  min="5"
                  max="20"
                  step="1"
                  value={formData.emergencyFundPercent}
                  onChange={handleInputChange}
                  className="w-full max-w-xs h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-600"
                />
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {formData.emergencyFundPercent}%
                </span>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Рекомендуется иметь от 10% до 15% от общей стоимости поездки на непредвиденные расходы
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalOptionsSection;